import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Tenant {
  id: string
  name: string
  slug: string
  status: string
  plan: string
  users: number
  storage_used_gb: number
  storage_limit_gb: number
  api_calls_month: number
  api_limit: number
  settings: any
}

export default function MultiTenantPage() {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)
  const [usage, setUsage] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [tenRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/tenants'),
      fetch(API_BASE + '/tenants/statistics')
    ])
    if (tenRes.ok) setTenants((await tenRes.json()).tenants || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const viewUsage = async (tenant: Tenant) => {
    setSelectedTenant(tenant)
    const res = await fetch(API_BASE + '/tenants/' + tenant.id + '/usage')
    if (res.ok) setUsage(await res.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'trial': return 'bg-blue-100 text-blue-800'
      case 'suspended': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-100 text-purple-800'
      case 'professional': return 'bg-blue-100 text-blue-800'
      case 'starter': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Multi-Tenant Management</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Tenants</div>
              <div className="text-2xl font-bold">{statistics.total_tenants}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_tenants}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Trial</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.trial_tenants}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Users</div>
              <div className="text-2xl font-bold">{statistics.total_users}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Storage Used</div>
              <div className="text-2xl font-bold">{statistics.total_storage_gb} GB</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Tenants</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Tenant</button>
            </div>
            <div className="divide-y">
              {tenants.map(tenant => (
                <div key={tenant.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => viewUsage(tenant)}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{tenant.name}</div>
                      <div className="text-sm text-gray-500">{tenant.slug}</div>
                    </div>
                    <div className="flex gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(tenant.status)}`}>{tenant.status}</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${getPlanColor(tenant.plan)}`}>{tenant.plan}</span>
                    </div>
                  </div>
                  <div className="flex gap-6 text-sm text-gray-500">
                    <span>{tenant.users} users</span>
                    <span>{tenant.storage_used_gb} / {tenant.storage_limit_gb} GB</span>
                    <span>{(tenant.api_calls_month / 1000).toFixed(0)}k API calls</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            {selectedTenant && usage ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedTenant.name}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Storage</span>
                      <span>{usage.storage?.percentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${usage.storage?.percentage > 80 ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${usage.storage?.percentage}%` }}></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{usage.storage?.used_gb} / {usage.storage?.limit_gb} GB</div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">API Calls</span>
                      <span>{usage.api?.percentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${usage.api?.percentage > 80 ? 'bg-red-500' : 'bg-green-600'}`} style={{ width: `${usage.api?.percentage}%` }}></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{usage.api?.calls_month?.toLocaleString()} / {usage.api?.limit?.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Settings</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>SSO Enabled</span>
                        <span className={selectedTenant.settings?.sso_enabled ? 'text-green-600' : 'text-gray-400'}>{selectedTenant.settings?.sso_enabled ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Custom Domain</span>
                        <span>{selectedTenant.settings?.custom_domain || 'None'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Data Retention</span>
                        <span>{selectedTenant.settings?.data_retention_days} days</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Tenant Details</h3>
                <p className="text-gray-500 text-sm">Select a tenant to view usage</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
